import React, { useMemo, useState } from "react";

import { Theme, Button, MenuItem, ListItemIcon, ListItemText, List, ListItemSecondaryAction, makeStyles } from '@material-ui/core';

import EditIcon from '@material-ui/icons/Edit';
import TemplateIcon from '@material-ui/icons/FileCopy';
import UploadIcon from '@material-ui/icons/CloudUpload';
import LinkIcon from '@material-ui/icons/InsertLink';
import WordLogo from '../../../../resources/file-word.png';
import SoundCloudLogo from '../../../../resources/soundcloud.png';
import VimeoLogo from '../../../../resources/vimeo.png';
import YouTubeLogo from '../../../../resources/youtube.png';
import { useOfflineAssets } from "../../../offline/useOfflineAssets";
import { AssetCategory } from "../../../../entities/AssetCategory";
import { AssetCategoryType, AssetDataType } from "../../../../entities/AssetType";
import { useAuthenticatedUser } from "../../../../utilities/useAuthenticatedUser";
import {YoutubeLinkDialog} from "./YoutubeLinkDialog";
import {SoundCloudLinkDialog} from "./SoundCloudLinkDialog";
import {VimeoLinkDialog} from "./VimeoLinkDialog";
import { AddDocxDialog } from "./AddDocxDialog";
import { CreateAssetDialog } from "./CreateAssetDialog";
import { AddNormalLinkDialog } from "./AddNormalLinkDialog";
import { UploadFileDialog } from "./UploadFileDialog";
import { AddFromTemplateDialog } from "../AddFromTemplateDialog/AddFromTemplateDialog";
import { AddAssetAction } from "../../../../entities/AddAssetAction";

const useStyles = makeStyles((theme: Theme) => ({
    menuItem: {
        minWidth: "260px",
    },
    youtubeIcon: {
        marginLeft: -4,
        marginRight: 8,
        minWidth: 0,
        "& img": {
            height: 36,
            width: 36
        }
    },
    vimeoIcon: {
        height: 28,
        width: 28,
        marginRight: 13,
        backgroundColor: "rgb(103, 103, 103)",
        borderRadius: 14,
        minWidth: 0,
        "& img": {
            height: 20,
            width: 20,
            paddingTop: 4,
            paddingLeft: 3
        }
    },
    soundCloudIcon: {
        border: "1px solid rgb(103, 103, 103)",
        borderRadius: 20,
        marginLeft: -4,
        minWidth: 0,
        "& + div": {
            paddingLeft: 10
        }
    },
    upgradeButton: {
        paddingRight: 15
    },
}));

interface Props {
    onCloseAll: () => void;
    selectedMenuCategoryType: AssetCategoryType;
}

const onUpsellOpen = (path:string, assetAction: AddAssetAction) => {
    window.location.assign(`https://www.sermonmaker.net/upgrade-now/?upgradePath=${path}&upgradeAction=${assetAction}`);
}

export function SecondLayerMenu(props:Props){
    const { onlineMode } = useOfflineAssets();
    const { selectedMenuCategoryType: {supportedTypes, category} } = props;
    const user = useAuthenticatedUser();
    const classes = useStyles();

    const [showYouTubeDialog, setShowYouTubeDialog] = useState(false);
    const [showSoundCloudDialog, setShowSoundCloudDialog] = useState(false);
    const [showVimeoDialog, setShowVimeoDialog] = useState(false);
    const [showLinkDialog, setShowLinkDialog] = useState(false);
    const [showDocxDialog, setShowDocxDialog] = useState(false);
    const [showUploadFileDialog, setShowUploadFileDialog] = useState(false);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showUseTemplateDialog, setShowUseTemplateDialog] = useState(false);

    const activeMakerSubscription = useMemo(() => user.makerSubscription !== undefined, [user]);
    
    return (
        <>
            <List style={{ marginTop: "8px", marginBottom: "8px" }}>
                {supportedTypes.some(type => type === AssetDataType.EditableText)
                    &&
                    <>
                        <MenuItem
                            disabled={!onlineMode}
                            className={classes.menuItem}
                            onClick={() => setShowCreateDialog(true)}
                        >
                            <ListItemIcon>
                                <EditIcon />
                            </ListItemIcon>
                            <ListItemText primary="Create from blank document" />
                        </MenuItem>
                        { category !== AssetCategory.Audio && category !== AssetCategory.Video && category !== AssetCategory.Notes && category !== AssetCategory.SermonSlides &&  
                            <MenuItem
                                disabled={!onlineMode}
                                className={classes.menuItem}
                                onClick={() => setShowUseTemplateDialog(true)}
                            >
                                <ListItemIcon>
                                    <TemplateIcon />
                                </ListItemIcon>
                                <ListItemText primary="Create from template" />
                            </MenuItem>
                        }
                    </>
                }
                {
                    supportedTypes.some(type => type === AssetDataType.File)
                    &&
                    <MenuItem
                        disabled={!onlineMode}
                        className={classes.menuItem}
                        onClick={() => {setShowUploadFileDialog(true)}}
                    >
                        <ListItemIcon>
                            <UploadIcon />
                        </ListItemIcon>
                        <ListItemText primary="Upload existing" />
                    </MenuItem>
                }
                {
                    supportedTypes.some(type => type === AssetDataType.Link)
                    &&
                    <MenuItem
                        disabled={!onlineMode}
                        className={classes.menuItem}
                        onClick={() => setShowLinkDialog(true)}
                    >
                        <ListItemIcon>
                            <LinkIcon />
                        </ListItemIcon>
                        <ListItemText primary="Add a link" />
                    </MenuItem>
                }
                {
                    supportedTypes.some(type => type === AssetDataType.EditableText)
                    &&
                    <MenuItem
                        disabled={!onlineMode}
                        className={classes.menuItem}
                        onClick={() => activeMakerSubscription 
                            ? setShowDocxDialog(true)
                            : onUpsellOpen("Word", AddAssetAction.AddDocUpload)}
                    >
                        <ListItemIcon>
                            <img alt="Word" src={WordLogo} />
                        </ListItemIcon>
                        <ListItemText primary="Upload Word Doc" />
                        {!activeMakerSubscription
                            &&
                            <ListItemSecondaryAction className={classes.upgradeButton}>
                                <Button 
                                    disabled={!onlineMode}
                                    variant="contained" color="primary" size="small" 
                                    onClick={() => onUpsellOpen("Word", AddAssetAction.AddDocUpload)}
                                    >
                                    Upgrade
                                </Button>
                            </ListItemSecondaryAction>
                        }
                    </MenuItem>
                }
                {category === AssetCategory.Video
                    &&
                    <MenuItem
                        disabled={!onlineMode}
                        className={classes.menuItem}
                        onClick={() => activeMakerSubscription 
                            ? setShowYouTubeDialog(true)
                            : onUpsellOpen("YouTube", AddAssetAction.AddYoutubeLink)}
                    >
                        <ListItemIcon className={classes.youtubeIcon}>
                            <img alt="YouTube" src={YouTubeLogo} />
                        </ListItemIcon>
                        <ListItemText primary="YouTube" />
                        {!activeMakerSubscription
                            &&
                            <ListItemSecondaryAction className={classes.upgradeButton}>
                                <Button
                                    disabled={!onlineMode} 
                                    variant="contained" color="primary" size="small" 
                                    onClick={() => onUpsellOpen("YouTube", AddAssetAction.AddYoutubeLink)}
                                >
                                    Upgrade
                                </Button>
                            </ListItemSecondaryAction>
                        }
                    </MenuItem>
                }
                {category === AssetCategory.Video
                    &&
                    <MenuItem
                        disabled={!onlineMode}
                        className={classes.menuItem}
                        onClick={() => activeMakerSubscription 
                            ? setShowVimeoDialog(true) 
                            : onUpsellOpen("Vimeo", AddAssetAction.AddVimeoLink)}
                    >
                        <ListItemIcon className={classes.vimeoIcon} >
                            <img alt="Vimeo" src={VimeoLogo} />
                        </ListItemIcon>
                        <ListItemText primary="Vimeo" />
                        {!activeMakerSubscription
                            &&
                            <ListItemSecondaryAction className={classes.upgradeButton}>
                                <Button 
                                    disabled={!onlineMode}
                                    variant="contained" color="primary" size="small" 
                                    onClick={() => onUpsellOpen("Vimeo", AddAssetAction.AddVimeoLink)}
                                >
                                    Upgrade
                                </Button>
                            </ListItemSecondaryAction>
                        }
                    </MenuItem>
                }
                {category === AssetCategory.Audio
                    &&
                    <MenuItem
                        disabled={!onlineMode}
                        className={classes.menuItem}
                        onClick={() => activeMakerSubscription 
                            ? setShowSoundCloudDialog(true)
                            : onUpsellOpen("SoundCloud", AddAssetAction.AddSoundCloudLink)}
                    >
                        <ListItemIcon className={classes.soundCloudIcon} >
                            <img alt="SoundCloud" src={SoundCloudLogo} />
                        </ListItemIcon>
                        <ListItemText primary="SoundCloud" />
                        {!activeMakerSubscription
                            &&
                            <ListItemSecondaryAction className={classes.upgradeButton}>
                                <Button 
                                    disabled={!onlineMode}
                                    variant="contained" color="primary" size="small" 
                                    onClick={() => onUpsellOpen("SoundCloud", AddAssetAction.AddSoundCloudLink)}                                    >
                                    Upgrade
                                </Button>
                            </ListItemSecondaryAction>
                        }
                    </MenuItem>
                }
            </List>
            {showCreateDialog && <CreateAssetDialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} selectedAssetType={props.selectedMenuCategoryType} />}
            {showUseTemplateDialog && <AddFromTemplateDialog open={showUseTemplateDialog} onClose={() => setShowUseTemplateDialog(false)} selectedAssetType={props.selectedMenuCategoryType} />}
            {showYouTubeDialog && <YoutubeLinkDialog open={showYouTubeDialog} onClose={() => setShowYouTubeDialog(false)} onSuccess={props.onCloseAll} />}
            {showSoundCloudDialog && <SoundCloudLinkDialog open={showSoundCloudDialog} onClose={() => setShowSoundCloudDialog(false)} onSuccess={props.onCloseAll} />}
            {showVimeoDialog && <VimeoLinkDialog open={showVimeoDialog} onClose={() => setShowVimeoDialog(false)} onSuccess={props.onCloseAll} />}
            {showLinkDialog && <AddNormalLinkDialog open={showLinkDialog} selectedAssetType={props.selectedMenuCategoryType} onClose={() => setShowLinkDialog(false)} onSuccess={props.onCloseAll} />}
            {showDocxDialog && <AddDocxDialog open={showDocxDialog} selectedAssetType={props.selectedMenuCategoryType} onClose={() => setShowDocxDialog(false)} onSuccess={props.onCloseAll}  />}
            {showUploadFileDialog && <UploadFileDialog open={showUploadFileDialog} onClose={() => setShowUploadFileDialog(false)} selectedAssetType={props.selectedMenuCategoryType} onSuccess={props.onCloseAll}  />}
        </>
    );
}